import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '../../hooks/use-document-title';
import {
  ContentAndButtonsContainer,
  NavbarAndContentContainer,
} from '../../layout/layout-container.styled';
import { listItemMotion, Presence, Staleable, StateSwitch } from '../../../motion';
import { ListSkeleton } from '../dashboard/dashboard-shared-components/load-state';
import { ScreenHeader } from '../../layout/page-header';
import { DefaultComponentProps } from '../../app-router/component-props';
import { ScanIdButton } from '../../capture/scan-id-button';
import { getBuildingRoster } from '../../../api/hierarchy-client';
import { signVisitorOut, VisitorExitError } from '../../../api/visitor-log-client';
import { VisitorInside } from '../../../interfaces/hierarchy-interfaces';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import {
  describeArrival,
  describeArrivalMeta,
  describeStale,
  filterArrivals,
  matchScannedName,
  ScanOutcome,
} from './visitor-exit-match';
import { arrivedText } from '../../../function-utils/building-time';
import { useClock } from '../../../context/clock-context';
import {
  Actions,
  Banner,
  Body,
  Card,
  CardTitle,
  Confirm,
  ConfirmText,
  Empty,
  ExitItem,
  Hint,
  Input,
  Label,
  Pill,
  Row,
  RowAction,
  RowLead,
  RowMain,
  RowMark,
  RowMeta,
  RowName,
  ScanRow,
  SearchRow,
  Stat,
  Stats,
  bannerMotion,
  confirmMotion,
} from './visitor-exit-page.styled';
import { initialsOf } from './desk-list';

// ---------------------------------------------------------------------------
// COMING OUT — the visitor exit desk.
// ---------------------------------------------------------------------------
//
// Wave A shipped POST /visitor-log/:id/exit and nothing ever called it: the
// board could name the arrivals nobody closed and offered no way to close one.
// This is that way.
//
// ============ THE SCAN SELECTS. THE DESK SIGNS OUT. ============
//
// A scanned document narrows the list to the people it might be. It never
// presses the button. Two reasons, and both are the kind that only show up
// afterwards:
//
//   1. A departure is a chain-of-custody record — `exit_source='desk'` claims a
//      human OBSERVED somebody leave. A model's reading of a worn licence is
//      not an observation, and the column exists precisely so a cleanup can
//      never be confused with one.
//   2. Names are not unique. Two Marias are signed in; the scan finds both; a
//      screen that auto-closed "the match" would close the wrong arrival and
//      leave the right one open, which is the exact failure the parcel desk was
//      built to avoid on the way in.
//
// ============ IT TARGETS A ROW, NOT A NAME ============
//
// Visitor names are AES-GCM with a random IV (migration 040), so the server
// cannot match one. The presence endpoint returns the open arrivals with names
// already decrypted, matching happens here, and what goes back is an id.
// Same person in and out twice today = two rows, each closed separately.

/* Layout animation (survivors sliding up when one leaves) is for a list you
   can read at a glance; past this many rows the reflow costs more than it
   says, so the rows still fade but stop measuring each other. */
const LAYOUT_CAP = 60;

const EXIT_HINT =
  'Scan the visitor’s ID or search the list, then confirm. Signing out records that YOU saw them leave, so nothing here happens on a scan alone.';

// The rail, the building panel and <main> come from DeskShell now
// (layout/desk-shell.tsx); this screen renders only what goes inside the card.
export const VisitorExitPage: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
}) => {
  useDocumentTitle('Visitors out');

  const buildingId = Number(selectedBuilding?.id) || null;
  /* A desk screen: arrivals are shown on the BUILDING's clock for whoever is at
     the desk (R-T12), from the instant — not from the text the server rendered
     on the reader's own clock. */
  const clock = useClock({ buildingId, buildingZone: selectedBuilding?.timezone });
  const { permissions } = useAuth();
  // The server enforces both of these. Checking here only decides whether to
  // explain instead of firing a request that is going to 403.
  const canSignOut = can(permissions, PERMISSIONS.VISITORLOG_CREATE);

  const [rows, setRows] = useState<VisitorInside[] | null>(null);
  // null = the caller may not see visitor rows at all (no visitorlog:read), so
  // the section is explained rather than shown empty. [] = allowed, and nobody
  // is inside. Those are different answers and must not render the same way.
  const [allowedToList, setAllowedToList] = useState(true);
  const [staleCount, setStaleCount] = useState(0);
  const [loading, setLoading] = useState(false);
  /* ── ONE READ WINS, AND IT IS THE LATEST ONE ─────────────────────────────
     refresh() is called on mount, after every sign-out and after every 409, so
     two are routinely in the air. Without a sequence number the LAST RESPONSE
     wins rather than the LATEST REQUEST: an older read that overtakes a newer
     one puts a visitor the desk has just signed out back on the list, with a
     dead arrival id behind the button. Same guard, same reason, as the resident
     screen's. */
  const readSeq = useRef(0);
  /* THE SECOND TAP ON THE SAME CONFIRM. `pending` below is state, so two
     presses inside one React commit both see it false; this ref is read and
     written synchronously and is what actually refuses the duplicate. Keyed on
     the ARRIVAL so two different rows never block each other. */
  const inFlight = useRef<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    tone: 'brand' | 'warning' | 'success';
    text: string;
  } | null>(null);

  const [query, setQuery] = useState('');
  // The row the desk has asked to sign out, awaiting the second tap.
  const [confirming, setConfirming] = useState<number | null>(null);
  /* PER ARRIVAL, NOT ONE AT A TIME. This was a single boolean and it disabled
     EVERY row's button while any one request was in the air — so a desk with
     three people walking out had to wait for each round trip before it could
     start the next, which is precisely the moment somebody starts tapping
     harder. The ref above is what makes a repeat tap on the SAME row a no-op;
     this only decides what is painted busy. */
  const [pending, setPending] = useState<ReadonlySet<number>>(() => new Set());
  // The row a scan picked out, so it can be marked in the list.
  const [scanPicked, setScanPicked] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!buildingId) return;
    const seq = ++readSeq.current;
    setLoading(true);
    try {
      const roster = await getBuildingRoster(buildingId);
      if (seq !== readSeq.current) return;
      const inside = roster.visitors_inside;
      setAllowedToList(inside !== null && inside !== undefined);
      setRows(inside ?? []);
      setStaleCount(roster.visitors_inside_stale_count ?? 0);
      setError(null);
    } catch {
      if (seq !== readSeq.current) return;
      setError('Could not load who is still inside.');
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyScan = (outcome: ScanOutcome) => {
    setConfirming(null);
    if (outcome.kind === 'none') {
      setScanPicked(null);
      setQuery('');
      setNotice({
        tone: 'warning',
        text: outcome.scanned
          ? `Nobody signed in under “${outcome.scanned}” is still inside. They may already have been signed out, or they arrived under a different name — search the list below.`
          : 'The document was read but carried no name. Search the list below.',
      });

      return;
    }
    // The scanned name becomes the SEARCH, so the desk can see what was matched
    // and can widen it by editing the box rather than by scanning again.
    setQuery(outcome.scanned);
    if (outcome.kind === 'one') {
      setScanPicked(outcome.row.id);
      setNotice({
        tone: 'brand',
        text: `Matched ${describeArrival(outcome.row)}. Check it is the right arrival, then sign them out.`,
      });

      return;
    }
    setScanPicked(null);
    setNotice({
      tone: 'warning',
      text: `${outcome.rows.length} people signed in under “${outcome.scanned}” are still inside. Pick the right arrival — signing out the wrong one leaves the right one open.`,
    });
  };

  const submitExit = async (row: VisitorInside) => {
    // THE GATE IS THE REF, THE PAINT IS THE STATE. A double-tap on Confirm used
    // to send two POSTs; the second came back 409 ALREADY_DEPARTED and the desk
    // was told "somebody at the desk already signed this visitor out" about
    // their own first tap — a warning that reads as a colleague's action.
    if (inFlight.current.has(row.id)) return;
    inFlight.current.add(row.id);
    setPending(prev => new Set(prev).add(row.id));
    setError(null);
    try {
      await signVisitorOut(row.id);
      setNotice({
        tone: 'success',
        text: `${describeArrival(row)} signed out.`,
      });
      setConfirming(null);
      setScanPicked(null);
      setQuery('');
      await refresh();
    } catch (e) {
      const err = e as VisitorExitError;
      if (err.code === 'ALREADY_DEPARTED') {
        // Not a failure the desk caused. Say which kind of departure already
        // exists — a colleague's observation and the nightly sweep's cleanup
        // are different facts and only one of them means somebody watched.
        setNotice({
          tone: 'warning',
          text:
            err.exitSource === 'auto'
              ? 'This arrival was already closed by the nightly sweep, not by anybody at the desk. Nothing was changed.'
              : 'Somebody at the desk already signed this visitor out. Nothing was changed.',
        });
        setConfirming(null);
        await refresh();

        return;
      }
      setError(err.message || 'Could not record the departure.');
    } finally {
      inFlight.current.delete(row.id);
      setPending(prev => {
        const next = new Set(prev);
        next.delete(row.id);

        return next;
      });
    }
  };

  const visible = rows ? filterArrivals(rows, query) : [];

  // ONE WORD FOR WHAT THE LIST IS, keying the crossfade below. `rows === null`
  // is "nothing has answered yet": a skeleton, unless the first read failed —
  // then the banner above has said so and the box stays empty rather than
  // pretending the lobby is clear. A refresh keeps the rows and dims them.
  const listState = !allowedToList
    ? 'denied'
    : rows === null
      ? error
        ? 'failed'
        : 'loading'
      : rows.length === 0
        ? 'empty'
        : visible.length === 0
          ? 'nomatch'
          : 'ready';

  const renderList = () => {
    if (listState === 'loading') {
      return <ListSkeleton rows={3} label="who is still inside" />;
    }

    if (listState === 'failed') return null;

    if (!allowedToList) {
      return (
        <Empty>
          You can sign a visitor out, but you cannot see the list of who is
          inside — that needs the visitor-log read permission. Ask an owner or
          property manager on your team to grant it.
        </Empty>
      );
    }

    if (rows && rows.length === 0) {
      return <Empty>Nobody is signed in. The lobby is clear.</Empty>;
    }

    if (visible.length === 0) {
      return (
        <Empty>
          Nobody still inside matches “{query}”. They may already have been
          signed out, or they signed in under a different name.
          {/* See the note on the activity desk: the miss was explained and the
              query left in place with nothing to press. */}
          <RowAction
            type="button"
            variant="quiet"
            onClick={() => setQuery('')}
          >
            Clear search
          </RowAction>
        </Empty>
      );
    }

    // Rows arrive in order and, in the Presence, a signed-out arrival leaves
    // rather than vanishing; layout stays on while the list is short enough
    // for the survivors' slide to read.
    return (
      <Staleable stale={loading}>
        <Presence>
          {visible.map((row, index) => {
      const stale = describeStale(row);
      const label = describeArrival(row);
      const tone = scanPicked === row.id ? 'picked' : row.stale ? 'stale' : 'plain';

      return (
        <ExitItem
          key={row.id}
          {...listItemMotion(index)}
          layout={visible.length <= LAYOUT_CAP}
        >
          <Row tone={tone}>
            <RowLead>
              <RowMark tone={tone} aria-hidden="true">
                {initialsOf(label)}
              </RowMark>
              <RowMain>
                <RowName>{label}</RowName>
                <RowMeta>{describeArrivalMeta(row, clock.zone)}</RowMeta>
                {stale ? <Pill tone="warning">{stale}</Pill> : null}
              </RowMain>
            </RowLead>
            <Actions>
              {/* ONE BUTTON THAT TOGGLES, not two side by side: the second tap
                  is the confirm below, and a permanent Cancel beside a
                  permanent Sign out is two controls for a decision that has
                  not been started yet. */}
              <RowAction
                type="button"
                variant={confirming === row.id ? 'quiet' : 'primary'}
                disabled={!canSignOut || pending.has(row.id)}
                onClick={() =>
                  setConfirming(confirming === row.id ? null : row.id)
                }
              >
                {confirming === row.id ? 'Cancel' : 'Sign out'}
              </RowAction>
            </Actions>
          </Row>
          <Presence>
            {confirming === row.id ? (
              <Confirm key="confirm" {...confirmMotion}>
                <ConfirmText>
                  {row.stale
                    ? `Record that ${label} left? This arrival is from ${arrivedText(row, clock.zone)} and nobody signed them out — if you did not see them leave, leave it open.`
                    : `Record that ${label} left, now?`}
                </ConfirmText>
                <RowAction
                  type="button"
                  variant="danger"
                  disabled={pending.has(row.id)}
                  onClick={() => submitExit(row)}
                >
                  {pending.has(row.id) ? 'Recording…' : 'Confirm sign-out'}
                </RowAction>
              </Confirm>
            ) : null}
          </Presence>
        </ExitItem>
      );
          })}
        </Presence>
      </Staleable>
    );
  };

  const count = rows ? rows.length : 0;
  // Narrowed vs total. Shown only when they differ, because "7 of 7" is a
  // sentence that says nothing and still costs a tile.
  const narrowed = query.trim() ? visible.length : null;

  return (
    <NavbarAndContentContainer>
          <ContentAndButtonsContainer>
            <Body>
              <ScreenHeader
                title="Coming out"
                subtitle={
                  selectedBuilding?.property_name || 'No building selected'
                }
              />

              <Presence>
                {error ? (
                  <Banner key="error" tone="alert" role="alert" {...bannerMotion}>
                    {error}
                  </Banner>
                ) : null}
                {notice ? (
                  <Banner
                    key="notice"
                    tone={notice.tone}
                    role="status"
                    {...bannerMotion}
                  >
                    {notice.text}
                  </Banner>
                ) : null}
              </Presence>

              {/* ---------- THE SCAN ---------- */}
              <Card>
                <CardTitle>Who is leaving?</CardTitle>
                <Hint>{EXIT_HINT}</Hint>
                <ScanRow>
                  <ScanIdButton
                    mode="id"
                    buildingId={buildingId}
                    label="Scan ID"
                    disabled={!buildingId || !allowedToList}
                    onResult={r => {
                      // IT FILLS THE SEARCH AND STORES NOTHING. No document
                      // image, no extracted fields, no provenance row for the
                      // scan itself: the exit record carries who was at the
                      // desk, not what a camera saw.
                      applyScan(
                        matchScannedName(rows ?? [], r.first_name, r.last_name)
                      );
                    }}
                  />
                </ScanRow>
              </Card>

              {/* ---------- STILL INSIDE ---------- */}
              <Card>
                <CardTitle>Still inside</CardTitle>
                {/* THE NUMBERS, AS TILES. The count used to be a parenthesis in
                    the heading and the stale count an amber paragraph under it —
                    two numbers in two shapes, one of which had to be read as a
                    sentence. The stale figure sits BESIDE the total and is never
                    subtracted from it: both are true and they answer different
                    questions, how many arrivals are open and how many of them
                    anybody actually saw. */}
                <Stats>
                  <Stat tone={count > 0 ? 'brand' : 'plain'}>
                    <b>{count}</b>
                    <span>Still inside</span>
                  </Stat>
                  <Stat tone={staleCount > 0 ? 'warning' : 'plain'}>
                    <b>{staleCount}</b>
                    <span>Never signed out</span>
                  </Stat>
                  <Stat>
                    <b>{narrowed ?? count}</b>
                    <span>{narrowed === null ? 'Showing' : 'Matching'}</span>
                  </Stat>
                </Stats>
                {/* Part of the first paint, so it does not animate in on
                    top of the page transition (initial={false}). */}
                <Presence initial={false}>
                  {staleCount > 0 ? (
                    <Banner key="stale" tone="warning" role="status" {...bannerMotion}>
                      {staleCount === 1
                        ? '1 of these arrivals is from an earlier day and was never signed out. It is not evidence anybody is still in the building.'
                        : `${staleCount} of these arrivals are from earlier days and were never signed out. They are not evidence anybody is still in the building.`}
                    </Banner>
                  ) : null}
                </Presence>
                <SearchRow>
                  <Label htmlFor="visitor-exit-search">Search</Label>
                  <Input
                    id="visitor-exit-search"
                    value={query}
                    placeholder="Name or unit"
                    onChange={e => {
                      setQuery(e.target.value);
                      setScanPicked(null);
                      setConfirming(null);
                    }}
                  />
                </SearchRow>
                {/* Skeleton, the explanations and the rows are fills of ONE
                    box that crossfade; the floor is three skeleton rows. */}
                <StateSwitch state={listState} minHeight={192}>
                  {renderList()}
                </StateSwitch>
              </Card>
            </Body>
          </ContentAndButtonsContainer>
    </NavbarAndContentContainer>
  );
};
